import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { App } from './App';
import './index.css';

/**
 * REMOVING THE MOCKS IS ONE LINE — the `enableMocks()` call in `bootstrap()` below.
 *
 * `VITE_USE_MOCKS` takes three values (see `.env.example`):
 *   unset     nothing mocked; every request goes to Django through the Vite proxy
 *   partial   only the unbuilt surface (leads, clients, dashboard, notifications)
 *   true      everything, including login — runs with no backend at all
 *
 * It is a no-op unless the variable is set, so a production build never ships the
 * worker and `msw` is dynamic-imported out of the bundle entirely.
 */
/**
 * Remove a mock worker left registered by an earlier session.
 *
 * A service worker outlives the page that registered it, the dev server, and the
 * setting that asked for it. So a browser that once ran with mocks on keeps
 * answering from them after they are switched off -- and it answers with the
 * fixtures of the day it was registered, which is worse than either mocking or
 * not mocking. The symptom is a screen that works for everyone except the person
 * who saw the mocks first, usually as a 404 for a record that plainly exists.
 *
 * Cheap to do and it fails silently: nothing here is load-bearing.
 */
async function unregisterStaleMocks(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const stale = registrations.filter((registration) =>
      registration.active?.scriptURL.includes('mockServiceWorker'),
    );
    if (stale.length === 0) return;

    await Promise.all(stale.map((registration) => registration.unregister()));

    // Unregistering does not release the page the worker is ALREADY controlling, so
    // this load would still be answered from fixtures and the next one would not --
    // which is worse than either, because the bug appears to fix itself and then
    // returns. One reload, once, and only when something was actually removed.
    if (navigator.serviceWorker.controller) {
      window.location.reload();
    }
  } catch {
    // Not worth reporting. If it fails the app still runs, just against the mock.
  }
}

async function enableMocks(): Promise<void> {
  const mode = import.meta.env.VITE_USE_MOCKS;
  if (mode !== 'true' && mode !== 'partial') {
    await unregisterStaleMocks();
    return;
  }
  try {
    const { startMocks } = await import('@legalcrm/api/mocks');
    await startMocks();
  } catch (error) {
    // FAIL SOFT, DELIBERATELY. Service-worker registration fails for reasons that
    // have nothing to do with the app — a stale registration, a browser with
    // workers disabled, an insecure origin. Letting that reject would stop the app
    // ever mounting, and the symptom is a blank white page with one console line.
    // A missing mock is a much smaller problem than an app that does not boot.
    // eslint-disable-next-line no-console
    console.warn('[msw] Mocks could not start; continuing against the real API.', error);
  }
}

/**
 * A function rather than top-level `await`: the build targets browsers that predate
 * top-level await, and esbuild refuses it outright rather than down-levelling.
 *
 * Mocks start before the first render, or the first request races the worker
 * registration and goes to the network instead.
 */
function bootstrap(): void {
  void enableMocks().then(() => {
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
  });
}

bootstrap();
